/**
 * 国际化资源加载工具 - 对标 @gulibs/vgrove-i18n 优化版
 */

import { SimpleCache } from './utils';

/**
 * 虚拟模块资源接口
 */
interface VirtualModuleInterface {
    resources: Record<string, Record<string, unknown>>;
    supportedLocales: string[];
    keys: string[];
    config?: {
        supportedLocales: string[];
        basePath: string;
        localePattern: 'directory' | 'filename';
    };
}

/**
 * 资源加载器配置接口 - 与 vgrove-i18n 兼容
 */
export interface I18nLoaderConfig {
    /** 翻译资源基础路径 */
    basePath?: string;
    /** 支持的文件扩展名 */
    extensions?: string[];
    /** 语言代码提取模式 */
    localePattern?: 'directory' | 'filename';
    /** 默认语言代码 */
    defaultLocale?: string;
    /** 缓存时间 (毫秒) */
    cacheTime?: number;
    /** 是否启用缓存 */
    cache?: boolean;
    /** 是否启用调试模式 */
    debug?: boolean;
    /** 加载回调 */
    onLoad?: (locale: string, data: Record<string, unknown>) => void;
    /** 错误回调 */
    onError?: (locale: string, error: Error) => void;
    /** 自定义获取翻译资源函数 */
    fetchResources?: (locale: string, path: string) => Promise<Record<string, unknown>>;
}

/**
 * 内部配置接口（必需属性）
 */
interface InternalLoaderConfig {
    basePath: string;
    extensions: string[];
    localePattern: 'directory' | 'filename';
    defaultLocale: string;
    cacheTime: number;
    cache: boolean;
    debug: boolean;
    onLoad?: (locale: string, data: Record<string, unknown>) => void;
    onError?: (locale: string, error: Error) => void;
    fetchResources?: (locale: string, path: string) => Promise<Record<string, unknown>>;
}

/**
 * 资源加载器接口 - 与 vite-plugin-i18n 生成的接口兼容
 */
export interface I18nResourceLoader {
    /** 加载指定语言的资源 */
    loadResources: (locale: string) => Promise<Record<string, unknown>>;
    /** 预加载多个语言的资源 */
    preloadResources: (locales: string[]) => Promise<Record<string, Record<string, unknown>>>;
    /** 清除缓存 */
    clearCache: (locale?: string) => void;
    /** 获取配置 */
    getConfig: () => I18nLoaderConfig;
    /** 获取所有可用的键 */
    getAvailableKeys?: () => string[];
    /** 检查键是否存在 */
    hasKey?: (key: string) => boolean;
    /** 获取命名空间资源 */
    getNamespaceResources?: (locale: string, namespace: string) => Record<string, unknown> | undefined;
}

/**
 * 安全地导入虚拟模块
 */
async function loadVirtualModule(): Promise<VirtualModuleInterface | null> {
    try {
        // 使用正确的虚拟模块 ID，与插件定义一致
        const virtualModuleId = '@gulibs/i18n-locales';
        const virtualModule = await import(/* @vite-ignore */ virtualModuleId);

        return {
            resources: virtualModule.resources || {},
            supportedLocales: virtualModule.supportedLocales || [],
            keys: virtualModule.keys || [],
            config: virtualModule.config
        };
    } catch {
            // 虚拟模块不存在时返回 null
            return null;
        }
}

/**
 * 虚拟模块加载器 - 优先使用 vite-plugin-i18n 生成的资源
 */
export class ViteI18nLoader implements I18nResourceLoader {
    private config: InternalLoaderConfig;
    private cache: SimpleCache<string, Record<string, unknown>>;
    private virtualModuleResources?: Record<string, Record<string, unknown>>;
    private virtualModuleKeys?: string[];
    private virtualModuleSupportedLocales?: string[];
    private virtualModuleConfig?: {
        supportedLocales: string[];
        basePath: string;
        localePattern: 'directory' | 'filename';
    };

    constructor(config: I18nLoaderConfig = {}) {
        this.config = {
            basePath: '/locales',
            extensions: ['.json', '.ts', '.js'],
            localePattern: 'directory',
            defaultLocale: 'en',
            cacheTime: 60 * 60 * 1000, // 1小时
            cache: true,
            debug: false,
            ...config
        };

        this.cache = new SimpleCache<string, Record<string, unknown>>(this.config.cacheTime);
        this.initVirtualModule();
    }

    /**
     * 初始化虚拟模块，加载静态资源
     */
    private async initVirtualModule() {
        try {
            const virtualModule = await loadVirtualModule();
            if (virtualModule) {
                this.virtualModuleResources = virtualModule.resources;
                this.virtualModuleKeys = virtualModule.keys;
                this.virtualModuleSupportedLocales = virtualModule.supportedLocales;
                this.virtualModuleConfig = virtualModule.config;

                // 将资源缓存到加载器的cache中，确保同步访问
                Object.entries(virtualModule.resources).forEach(([locale, resources]) => {
                    if (this.config.cache) {
                        this.cache.set(locale, resources);
                    }
                });

                // Virtual module loaded successfully
            }
        } catch {
            // Failed to load virtual module, will fallback to dynamic loading
        }
    }

    /**
     * 获取虚拟模块资源（供外部直接访问）
     */
    getVirtualModuleResources(): Record<string, Record<string, unknown>> | undefined {
        return this.virtualModuleResources;
    }

    /**
     * 获取虚拟模块配置
     */
    getVirtualModuleConfig(): {
        supportedLocales: string[];
        basePath: string;
        localePattern: 'directory' | 'filename';
    } | undefined {
        return this.virtualModuleConfig;
    }

    /**
     * 加载指定语言的翻译资源
     */
    async loadResources(locale: string): Promise<Record<string, unknown>> {
        // 检查缓存
        if (this.config.cache && this.cache.has(locale)) {
            const cached = this.cache.get(locale);
            if (cached) return cached;
        }

        // 优先使用虚拟模块资源
        if (this.virtualModuleResources?.[locale]) {
            const resources = this.virtualModuleResources[locale];

            if (this.config.cache) {
                this.cache.set(locale, resources);
            }

            if (this.config.onLoad) {
                this.config.onLoad(locale, resources);
            }

            return resources;
        }

        // 回退到动态加载
        return this.dynamicLoadResources(locale);
    }

    /**
     * 动态加载资源（当虚拟模块不可用时的回退方案）
     */
    private async dynamicLoadResources(locale: string): Promise<Record<string, unknown>> {
        try {
            let resources: Record<string, unknown>;

            if (this.config.fetchResources) {
                // 使用自定义获取函数
                const path = `${this.config.basePath}/${locale}${this.config.extensions[0]}`;
                resources = await this.config.fetchResources(locale, path);
            } else {
                // 默认使用 fetch API
                const extension = this.config.extensions[0];
                const url = `${this.config.basePath}/${locale}${extension}`;
                const response = await fetch(url);

                if (!response.ok) {
                    throw new Error(`Failed to load locale ${locale}: ${response.statusText}`);
                }

                resources = await response.json();
            }

            // 缓存资源
            if (this.config.cache) {
                this.cache.set(locale, resources);
            }

            // 触发加载回调
            if (this.config.onLoad) {
                this.config.onLoad(locale, resources);
            }

            return resources;
        } catch (error) {
            // 触发错误回调
            if (this.config.onError && error instanceof Error) {
                this.config.onError(locale, error);
            }

            // Failed to load locale resources

            return {};
        }
    }

    /**
     * 预加载多个语言的翻译资源
     */
    async preloadResources(locales: string[]): Promise<Record<string, Record<string, unknown>>> {
        const resources: Record<string, Record<string, unknown>> = {};

        // 如果有虚拟模块资源，直接返回相关的资源
        if (this.virtualModuleResources) {
            locales.forEach(locale => {
                if (this.virtualModuleResources![locale]) {
                    resources[locale] = this.virtualModuleResources![locale];
                }
            });

            // 只加载虚拟模块中没有的语言
            const missingLocales = locales.filter(locale => !this.virtualModuleResources![locale]);
            if (missingLocales.length === 0) {
                return resources;
            }
            locales = missingLocales;
        }

        // 并行加载缺失的资源
        await Promise.all(
            locales.map(async (locale) => {
                try {
                    resources[locale] = await this.loadResources(locale);
                } catch {
                    // Failed to preload locale
                    resources[locale] = {};
                }
            })
        );

        return resources;
    }

    /**
     * 清除资源缓存
     */
    clearCache(locale?: string): void {
        if (locale) {
            this.cache.delete(locale);
        } else {
            this.cache.clear();
        }
    }

    /**
     * 获取配置
     */
    getConfig(): I18nLoaderConfig {
        return { ...this.config };
    }

    /**
     * 获取所有可用的键（来自虚拟模块）
     */
    getAvailableKeys(): string[] {
        return this.virtualModuleKeys || [];
    }

    /**
     * 检查键是否存在
     */
    hasKey(key: string): boolean {
        return this.virtualModuleKeys?.includes(key) || false;
    }

    /**
     * 获取命名空间资源
     */
    getNamespaceResources(locale: string, namespace: string): Record<string, unknown> | undefined {
        const localeResources = this.virtualModuleResources?.[locale];
        return localeResources?.[namespace] as Record<string, unknown> | undefined;
    }

    /**
     * 获取支持的语言列表
     */
    getSupportedLocales(): string[] {
        return this.virtualModuleSupportedLocales || [this.config.defaultLocale];
    }

    /**
     * 检查是否使用虚拟模块
     */
    isUsingVirtualModule(): boolean {
        return !!this.virtualModuleResources;
    }
}

/**
 * 创建优化的资源加载器
 */
export function createI18nLoader(config: I18nLoaderConfig = {}): I18nResourceLoader {
    return new ViteI18nLoader(config);
}